import { useEffect, useState } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faTimes, faImage, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import servAnalises from "../../../services/login/servAnalises";

const theme = {
  bg: "#131318",
  card: "#1c1c24",
  cardHover: "#23232e",
  border: "#2e2e3d",
  accent: "#7c4dff",
  accentLight: "#9c6fff",
  accentGlow: "rgba(124, 77, 255, 0.18)",
  textPrimary: "#f0f0f8",
  textSecondary: "#8888aa",
  textMuted: "#55556a",
  overlay: "rgba(10,10,16,0.92)",
};

const CardInfo = ({ titulo, valor, icon }) => (
  <div
    style={{
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: 10,
      padding: "10px 16px",
      minWidth: 130,
      flex: "1 1 130px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}
  >
    <div style={{ fontSize: 10, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
      {titulo}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
      {valor ?? "-"}
    </div>
  </div>
);

const ImagensCod = ({ setOpen, codigoCod, recordForView }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [imagens, setImagens] = useState([]);
  const [imagemAmpliada, setImagemAmpliada] = useState(null);
  const [indexAmpliado, setIndexAmpliado] = useState(null);
  const [erro, setErro] = useState("");

  const cabecalho = {
    solicitacao: recordForView?.CodigoSolicitacaoVistoria,
    protocolo: recordForView?.SolicitacaoVistoria?.Protocolo,
    interessado: recordForView?.SolicitacaoVistoria?.SolicitacaoDeAcesso?.Nome,
    anexos: imagens.length,
  };

  useEffect(() => {
    if (!codigoCod) return;
    const buscarImagens = async () => {
      try {
        setIsLoading(true);
        setErro("");
        const data = await servAnalises.recuperarImagensDaSolicitacao(codigoCod);
        setImagens(data.Content ?? []);
      } catch (err) {
        setErro("Erro ao carregar as imagens.");
      } finally {
        setIsLoading(false);
      }
    };
    buscarImagens();
  }, [codigoCod]);

  const abrirLightbox = (img, index) => {
    setImagemAmpliada(img);
    setIndexAmpliado(index);
  };

  const navegar = (direcao) => {
    const novoIndex = indexAmpliado + direcao;
    if (novoIndex >= 0 && novoIndex < imagens.length) {
      setImagemAmpliada(imagens[novoIndex]);
      setIndexAmpliado(novoIndex);
    }
  };

  const fecharLightbox = () => {
    setImagemAmpliada(null);
    setIndexAmpliado(null);
  };

  return (
    <div
      style={{
        width: "100%",
        minWidth: 680,
        maxWidth: 860,
        overflow: "hidden",
        background: theme.bg,
        borderRadius: 14,
        padding: "20px 24px 24px",
        boxSizing: "border-box",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <CardInfo titulo="Solicitação" valor={cabecalho.solicitacao} />
        <CardInfo titulo="Protocolo" valor={cabecalho.protocolo} />
        <CardInfo titulo="Interessado" valor={cabecalho.interessado} />
        <div
          style={{
            background: theme.accentGlow,
            border: `1px solid ${theme.accent}`,
            borderRadius: 10,
            padding: "10px 16px",
            minWidth: 90,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 10, color: theme.accentLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Anexos
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.accentLight }}>
            {cabecalho.anexos}
          </div>
        </div>
      </div>

      <div style={{ height: 1, background: theme.border, marginBottom: 20 }} />

      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 0" }}>
          <CircularProgress size={32} style={{ color: theme.accent }} />
          <span style={{ color: theme.textSecondary, fontSize: 13 }}>Carregando imagens...</span>
        </div>
      )}

      {!isLoading && erro && (
        <div
          style={{
            background: "rgba(220,50,50,0.1)",
            border: "1px solid rgba(220,50,50,0.3)",
            borderRadius: 8,
            padding: "12px 16px",
            color: "#ff6b6b",
            fontSize: 13,
          }}
        >
          {erro}
        </div>
      )}

      {!isLoading && !erro && imagens.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            padding: "48px 0",
            color: theme.textMuted,
          }}
        >
          <FontAwesomeIcon icon={faImage} style={{ fontSize: 36, opacity: 0.3 }} />
          <span style={{ fontSize: 13 }}>Nenhuma imagem encontrada</span>
        </div>
      )}

      {!isLoading && imagens.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            maxHeight: 340,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {imagens.map((img, index) => (
            <ImageCard
              key={index}
              img={img}
              index={index}
              onClick={() => abrirLightbox(img, index)}
            />
          ))}
        </div>
      )}

      {imagemAmpliada && (
        <div
          onClick={fecharLightbox}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: theme.overlay,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={fecharLightbox}
            style={{
              position: "fixed",
              top: 20,
              right: 24,
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${theme.border}`,
              borderRadius: "50%",
              width: 36,
              height: 36,
              cursor: "pointer",
              color: theme.textPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {indexAmpliado > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navegar(-1); }}
              style={navBtnStyle("left")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}

          <div
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: "center" }}
          >
            <img
              src={`data:${imagemAmpliada.MimeType ?? "image/jpeg"};base64,${imagemAmpliada.ImagemBase64}`}
              alt="Ampliada"
              style={{
                maxWidth: "88vw",
                maxHeight: "82vh",
                borderRadius: 10,
                boxShadow: `0 8px 60px rgba(0,0,0,0.7), 0 0 0 1px ${theme.border}`,
              }}
            />
            <div style={{ color: theme.textSecondary, marginTop: 10, fontSize: 12 }}>
              {imagemAmpliada.NomeImagem || imagemAmpliada.NomeArquivo || ""}
              {imagens.length > 1 && (
                <span style={{ marginLeft: 10, color: theme.textMuted }}>
                  {indexAmpliado + 1} / {imagens.length}
                </span>
              )}
            </div>
          </div>

          {indexAmpliado < imagens.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navegar(1); }}
              style={navBtnStyle("right")}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const navBtnStyle = (side) => ({
  position: "fixed",
  [side]: 24,
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(124, 77, 255, 0.25)",
  border: "1px solid rgba(124, 77, 255, 0.4)",
  borderRadius: "50%",
  width: 44,
  height: 44,
  cursor: "pointer",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  zIndex: 10000,
  transition: "background 0.15s",
});

const ImageCard = ({ img, index, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip title={img.NomeImagem || img.NomeArquivo || `Imagem ${index + 1}`} placement="top">
      <div
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderRadius: 10,
          overflow: "hidden",
          background: "#1c1c24",
          border: `1px solid ${hovered ? "rgba(124,77,255,0.5)" : "#2e2e3d"}`,
          cursor: "pointer",
          transition: "border-color 0.2s, transform 0.15s, box-shadow 0.2s",
          transform: hovered ? "translateY(-2px)" : "none",
          boxShadow: hovered ? "0 6px 20px rgba(124,77,255,0.15)" : "0 1px 4px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <div style={{ height: 130, position: "relative", overflow: "hidden" }}>
          <img
            src={`data:${img.MimeType ?? "image/jpeg"};base64,${img.ImagemBase64}`}
            alt={img.NomeImagem ?? `Imagem ${index + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.2s",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          />
          {hovered && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(124,77,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faExpand}
                style={{ color: "#fff", fontSize: 18, opacity: 0.9 }}
              />
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 10,
            color: "#8888aa",
            textAlign: "center",
            padding: "6px 8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            background: "#16161f",
            borderTop: "1px solid #2e2e3d",
          }}
        >
          {img.NomeImagem || img.NomeArquivo || `Imagem ${index + 1}`}
        </div>
      </div>
    </Tooltip>
  );
};

export { ImagensCod };